/**
 * 埋点看板（`/analytics`）。
 *
 * 这一屏是**唯一一处会把「产品假设是否成立」直接摆到人眼前的界面**，因此它的写法与其它页不同：
 *
 * 1. **样本量与结论同时呈现，且不可分离。** 接口专门返回了
 *    `readiness.overall.sufficientSample` 与 `disclaimer`；界面若只显示一个漂亮的达标率，
 *    读者会把「3 秒建立认知」当成已证实的结论。因此样本前提放在最上面（先于 KPI），
 *    达标率旁边永远跟着样本数，`sufficientSample` 为 false 时用醒目告示写明「不足以据此下结论」。
 *
 * 2. **判定标准不在前端。** 「样本多少算够」由服务端判定（sufficientSample），
 *    「多少毫秒算达标」由服务端给出（thresholdMs）；界面只呈现，不自己设阈值 ——
 *    阈值写死在两处，迟早出现「接口说不足、界面说足够」的分裂。
 *
 * 3. **缺失值不用 0 冒充。** 样本量为 0 时比率在数学上无定义（接口会把 rate 算成 0），
 *    界面此时显示「未提供」而不是「0.0%」：前者是「没有样本」，后者看起来像「全都不达标」。
 *
 * 4. **免责声明原样显示。** 埋点结论的限定条件写在服务端的 `disclaimer` 里，界面不改写、不节选。
 *
 * 权限：需要 `analytics:read`（数据运营、中台管理员、超级管理员具备）。没有该权限点时
 * 服务端返回 403，本页用**页面级 `PermissionBlock`** 照实说明，不显示空图表 ——
 * 「看不到」与「没有数据」是两件事。
 */
import { useState } from 'react';
import { adminApi, ApiError } from '../lib/api.ts';
import { useLoad } from '../lib/useLoad.ts';
import { Card, Kpi, PageHead, PermissionBlock } from '../components/ui.tsx';
import { EmptyBox, ErrorBox, LoadingBox, errorText } from '../components/StateBox.tsx';
import {
  MISSING_TEXT,
  analyticsEventLabel,
  formatDateTime,
  formatMs,
  formatRate,
  funnelLabel,
  funnelStepLabel,
  plainText,
  ratioText,
  sampleNotice,
} from '../lib/format.ts';

/**
 * 可选统计窗口。取值即接口的 `days` 参数（服务端限定 1–90，默认 7）；
 * 这里只列出常用的几档，越界值不会被送到服务端。
 */
const WINDOW_OPTIONS: { value: number; label: string }[] = [
  { value: 7, label: '近 7 天' },
  { value: 14, label: '近 14 天' },
  { value: 30, label: '近 30 天' },
  { value: 90, label: '近 90 天（接口上限）' },
];

/** 漏斗条形宽度：以首步会话数为基准（服务端按事件数倒序给出，首步即入口步）。 */
function barWidth(current: number, base: number): string {
  if (base <= 0) return '0%';
  const pct = Math.round((current / base) * 100);
  return `${Math.max(0, Math.min(100, pct))}%`;
}

export function AnalyticsPage() {
  const [days, setDays] = useState(7);
  const report = useLoad((signal) => adminApi.analytics(days, signal));

  if (report.loading) return <LoadingBox text="正在加载埋点指标…" />;

  if (report.error) {
    const err = report.error;
    // 403：角色缺 analytics:read（本接口不要求成员可见范围，返回的是聚合数据）
    if (err instanceof ApiError && err.status === 403) {
      return (
        <div>
          <PageHead title="埋点看板" sub="403 · 权限拦截" />
          <Card title="无权查看埋点指标" sub="需要 analytics:read 权限点">
            <PermissionBlock
              title="当前角色没有「analytics:read」权限"
              desc={`服务端返回：${err.message}。该权限点归属：数据运营（埋点与运营分析）与中台管理员，超级管理员亦具备；它只给聚合视角，不涉及任何成员个体数据，因此不要求守护关系。界面不会把这次拦截显示成「窗口内没有事件」——「看不到」与「没有数据」是两个结论。`}
            />
            <div className="sp12" />
            <button type="button" className="btn sm" onClick={report.reload}>
              重试
            </button>
          </Card>
        </div>
      );
    }
    return (
      <div>
        <PageHead title="埋点看板" sub="读取失败" />
        <ErrorBox message={errorText(err)} onRetry={report.reload} />
      </div>
    );
  }

  if (!report.data) {
    return (
      <div>
        <PageHead title="埋点看板" sub="接口未返回数据" />
        <ErrorBox message="埋点指标为空" onRetry={report.reload} />
      </div>
    );
  }

  const data = report.data;
  const overall = data.readiness.overall;
  const routes = data.readiness.routes;
  const counts = data.counts;
  const funnels = data.funnels;
  const sample = sampleNotice(overall.sufficientSample, overall.samples);
  // 样本为 0 时比率无定义：接口此时把 rate 算成 0，界面必须显示「未提供」而不是 0.0%
  const overallRate = overall.samples > 0 ? formatRate(overall.rate) : MISSING_TEXT;

  const switchWindow = (value: number): void => {
    setDays(value);
    // 同一次事件里改窗口 + 触发重取：useLoad 的 effect 会用最新渲染的 days 重新取数
    report.reload();
  };

  return (
    <div>
      <PageHead
        title="埋点看板"
        sub="数据来自客户端上报的埋点事件；本页只呈现聚合口径，样本不足时不下结论。"
        actions={<span className="badge ghost">统计窗口：近 {data.windowDays} 天</span>}
      />

      <Card tight>
        <div className="chip-row">
          {WINDOW_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`chip${days === option.value ? ' on' : ''}`}
              onClick={() => switchWindow(option.value)}
            >
              {option.label}
            </button>
          ))}
          <span className="text-tiny muted" style={{ alignSelf: 'center', marginLeft: 6 }}>
            窗口取值即接口参数 days（服务端限定 1–90，默认 7），当前生效：近 {data.windowDays} 天
          </span>
        </div>
      </Card>

      {/* 结论前提先于结论出现：样本量与是否足够必须与达标率同时在场 */}
      <Card title="结论前提（样本与口径）" sub="样本不足时，下面任何比率都不足以作为结论">
        <div className={`alert-chip ${sample.warn ? 'warn' : 'ok'}`}>
          <span>{sample.warn ? '⚠' : '✓'}</span>
          <span>
            <b>样本是否足够</b>
            <br />
            {sample.text}
          </span>
        </div>

        <div className="kv">
          <div>
            <div className="kv-l">本窗口样本数（page_ready 上报次数）</div>
            <div className="kv-v">{overall.samples}</div>
          </div>
          <div>
            <div className="kv-l">其中达标数（阈值内可见）</div>
            <div className="kv-v">
              {overall.within}
              <span className="muted text-tiny"> / {overall.samples}</span>
            </div>
          </div>
          <div>
            <div className="kv-l">整体达标率</div>
            <div className="kv-v">
              {overallRate}
              {overall.samples <= 0 && (
                <span className="muted text-tiny">（窗口内没有样本，比率无定义；接口此时给的是 0，界面不显示成 0%）</span>
              )}
            </div>
          </div>
          <div>
            <div className="kv-l">达标阈值 thresholdMs</div>
            <div className="kv-v">{formatMs(data.readiness.thresholdMs)}</div>
          </div>
          <div>
            <div className="kv-l">事件总量</div>
            <div className="kv-v">{data.totals.total}</div>
          </div>
          <div>
            <div className="kv-l">覆盖成员 / 会话</div>
            <div className="kv-v">
              {data.totals.members} / {data.totals.sessions}
            </div>
          </div>
        </div>

        <div className="note-src">
          <b>免责声明（服务端原文）</b>
          <br />
          {plainText(data.disclaimer)}
          <br />
          界面不做改写与节选；该声明与上方的样本判定一并阅读才有意义 ——
          不含样本量的达标率在本平台上不构成结论。
        </div>
      </Card>

      <div className="grid4">
        <Kpi label="事件总量" value={data.totals.total} delta={`近 ${data.windowDays} 天累计上报`} />
        <Kpi label="覆盖成员" value={data.totals.members} delta="去重后的上报成员数" />
        <Kpi label="会话数" value={data.totals.sessions} delta="去重后的会话数" />
        <Kpi
          label="整体达标率"
          value={overallRate}
          tone={overall.sufficientSample ? 'e' : 'warn'}
          delta={`样本 ${overall.samples} 个 · ${overall.sufficientSample ? '接口判定样本足够' : '接口判定样本不足'}`}
        />
      </div>

      <Card title="事件分布" sub="按上报次数倒序（顺序由服务端给出，界面不重排）">
        {counts.length === 0 ? (
          <EmptyBox
            title="窗口内没有任何事件"
            desc="这与「读不到数据」不同：接口确实返回了 0 条事件计数。可确认埋点是否启用、上报是否被限流（429）、事件名是否已登记。"
          />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>事件</th>
                  <th>事件名</th>
                  <th style={{ textAlign: 'right' }}>次数</th>
                  <th>最近上报</th>
                </tr>
              </thead>
              <tbody>
                {counts.map((row) => (
                  <tr key={row.name}>
                    <td>
                      <b>{analyticsEventLabel(row.name)}</b>
                    </td>
                    <td className="mono-num">{row.name}</td>
                    <td className="num-cell">{row.count}</td>
                    <td className="mono-num">{formatDateTime(row.lastSeen)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Card
        title="内容可见耗时"
        sub={`回答「是否在 ${formatMs(data.readiness.thresholdMs)} 内建立认知」· 样本与达标率逐路由列出`}
      >
        {routes.length === 0 ? (
          <EmptyBox
            title="窗口内没有内容可见（page_ready）上报"
            desc="没有样本就无法回答该问题 —— 这里不给出任何比率。可确认客户端是否在路由数据渲染完成后上报 page_ready。"
          />
        ) : (
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>路由</th>
                  <th style={{ textAlign: 'right' }}>样本数</th>
                  <th style={{ textAlign: 'right' }}>达标数</th>
                  <th style={{ textAlign: 'right' }}>达标率</th>
                  <th style={{ textAlign: 'right' }}>p50</th>
                  <th style={{ textAlign: 'right' }}>p90</th>
                  <th style={{ textAlign: 'right' }}>最慢</th>
                </tr>
              </thead>
              <tbody>
                {routes.map((row) => (
                  <tr key={row.route}>
                    <td className="mono-num">{row.route}</td>
                    <td className="num-cell">{row.samples}</td>
                    <td className="num-cell">{row.within3s}</td>
                    <td className="num-cell">{ratioText(row.within3s, row.samples)}</td>
                    <td className="num-cell">{formatMs(row.p50)}</td>
                    <td className="num-cell">{formatMs(row.p90)}</td>
                    <td className="num-cell">{formatMs(row.worst)}</td>
                  </tr>
                ))}
                <tr>
                  <td>
                    <b>合计（接口给出的整体口径）</b>
                  </td>
                  <td className="num-cell">{overall.samples}</td>
                  <td className="num-cell">{overall.within}</td>
                  <td className="num-cell">{overall.samples > 0 ? formatRate(overall.rate) : MISSING_TEXT}</td>
                  <td className="num-cell" colSpan={3}>
                    <span className="muted text-tiny">
                      分位数按路由各自计算，不做跨路由合并
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
        <div className="note-src">
          <b>怎么读这张表</b>
          <br />
          达标率的分母是<b>该路由的样本数</b>（上表第二列），不是事件总量；样本数少的行即使达标率是 100%
          也只能当排查线索。整体口径的样本是否足够由接口判定：
          {overall.sufficientSample ? '当前判定为足够。' : `当前判定为不足（${overall.samples} 个样本），据此下结论不成立。`}
        </div>
      </Card>

      <Card title="漏斗" sub="按接口给出的 funnels 结构渲染：漏斗 → 步骤（事件数 / 会话数 / 占首步比例）">
        {funnels.length === 0 ? (
          <EmptyBox title="接口未下发漏斗" desc="服务端返回的 funnels 为空数组；界面不自行拼装漏斗。" />
        ) : (
          funnels.map((funnel) => {
            const base = funnel.steps[0]?.sessions ?? 0;
            return (
              <div key={funnel.funnel}>
                <div className="card-tt">
                  <span className="bar" />
                  {funnelLabel(funnel.funnel)}
                  <small className="mono-num">{funnel.funnel}</small>
                </div>
                {funnel.steps.length === 0 ? (
                  <div className="state-box">
                    <div className="st-title">窗口内无该漏斗数据</div>
                    <div>服务端返回 0 个步骤；界面不补占位数字。</div>
                  </div>
                ) : (
                  <div className="tbl-wrap">
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>步骤</th>
                          <th>步骤名</th>
                          <th style={{ textAlign: 'right' }}>事件数</th>
                          <th style={{ textAlign: 'right' }}>会话数</th>
                          <th style={{ textAlign: 'right' }}>占首步比例</th>
                          <th>分布</th>
                        </tr>
                      </thead>
                      <tbody>
                        {funnel.steps.map((step) => (
                          <tr key={step.step}>
                            <td>
                              <b>{funnelStepLabel(step.step)}</b>
                            </td>
                            <td className="mono-num">{step.step}</td>
                            <td className="num-cell">{step.events}</td>
                            <td className="num-cell">{step.sessions}</td>
                            <td className="num-cell">{ratioText(step.sessions, base, 0)}</td>
                            <td>
                              <div className="funnel-track">
                                <div className="funnel-fill" style={{ width: barWidth(step.sessions, base) }} />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })
        )}
        <div className="note-src">
          <b>漏斗口径</b>
          <br />
          会话数为按会话去重后的到达数，事件数为全部上报次数（两者同时给出，避免只看得见其中一个）；
          「占首步比例」的分母是该漏斗第一步的会话数，服务端按事件数倒序返回步骤，界面不重排。
        </div>
      </Card>

      <div className="note-src">
        <b>权限与数据边界</b>
        <br />
        读取本页需要 <span className="mono-num">analytics:read</span> 权限点（数据运营、中台管理员、超级管理员具备）；
        该接口<b>不要求成员可见范围</b>，因为它只返回聚合数据。埋点不采集任何身份信息与健康数据
        （由上报接口逐条拒绝），因此本页也永远不会出现成员个体明细。
      </div>
    </div>
  );
}
